import type { IGhCard, IGhToolResultEnvelope } from '../gpthelp-host-contract';
import { clampText } from '../cards/card-text';

/** The tag of a host tool result envelope (host contract §3.2). */
export const TOOL_RESULT_ENVELOPE = 'tool-result/v1';
/** Cards one tool result may show. */
export const ENVELOPE_MAX_CARDS = 5;
/** Follow-up chips a tool result may set, and their length. */
export const ENVELOPE_MAX_FOLLOW_UPS = 3;
export const FOLLOW_UP_MAX_LENGTH = 60;
/** Length of the text a tool result may end the turn with. */
export const END_TURN_MAX_LENGTH = 280;

export interface IToolEnvelopeInput {
  /** What the model sees. */
  data: unknown;
  /** Shown to the user, never sent to the model. */
  cards?: IGhCard[];
  /** Ends the turn with this text, without another model round. */
  endTurnAfter?: string;
  /** Replaces the turn's follow-up chips. */
  followUps?: string[];
}

/** A host tool result with cards (host contract §3.2), within its caps. */
export function toolResultEnvelope(
  input: IToolEnvelopeInput
): IGhToolResultEnvelope {
  const envelope: IGhToolResultEnvelope = {
    gpthelp: TOOL_RESULT_ENVELOPE,
    data: input.data,
  };
  const cards = (input.cards ?? []).slice(0, ENVELOPE_MAX_CARDS);
  if (cards.length) envelope.cards = cards;
  const endText = clampText(input.endTurnAfter ?? '', END_TURN_MAX_LENGTH);
  if (endText) envelope.endTurnAfter = { text: endText };
  const followUps = (input.followUps ?? [])
    .map((chip) => clampText(chip, FOLLOW_UP_MAX_LENGTH))
    .filter((chip) => chip !== '')
    .slice(0, ENVELOPE_MAX_FOLLOW_UPS);
  if (followUps.length) envelope.followUps = followUps;
  return envelope;
}

export function isToolResultEnvelope(
  value: unknown
): value is IGhToolResultEnvelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    'gpthelp' in value &&
    value.gpthelp === TOOL_RESULT_ENVELOPE &&
    'data' in value
  );
}
